import type { Cart, DeliveryGroup } from '@/modules/cart/domain/Cart';
import type { CartLineItem } from '@/modules/cart/domain/CartLineItem';
import type {
  IGtmProductBilling,
  IGTMSendProductsBilling,
  IWebViewGtmProductBilling
} from '@/modules/perseo-billing/interfaces/src/front/interfaces/gtm-group';
import { GtmTypeUser } from '@/modules/perseo-billing/interfaces/src/front/interfaces/gtm-group';
import { GtmEventInteractiveAction } from '@/modules/perseo-billing/interfaces/src/front/interfaces/gtm-group';
import type { VariantPriceDto } from '@/modules/shared/types/PriceDto';
import { BILLING_ACTION_FIELD } from '@/modules/shared/utils/gtmEvents/events';

const getShippingMethodsLabelsByDeliveryGroups = (
  deliveryGroups?: DeliveryGroup[]
): string => {
  const shippingMethodsPerProduct = deliveryGroups?.map(
    (deliveryGroup) => deliveryGroup.shippingOption?.shippingMethod
  );
  return shippingMethodsPerProduct?.join(', ') || '';
};

const calculateEventPrice = (
  prices: VariantPriceDto,
  isPaidWithCencosud: boolean = false
) => {
  const { offer, paymentMethod, regular } = prices || {};
  const basePrice = offer || paymentMethod || regular;
  if (isPaidWithCencosud) return String(basePrice?.value?.centAmount ?? '');
  return String(basePrice?.value?.centAmount ?? '');
};
const createGTMProductObject = (
  item: CartLineItem,
  isCencoPrice: boolean
): IGtmProductBilling => {
  const { sku, prices } = item?.variant || {};
  const variationId = sku || '';
  const { regular } = prices || {};

  const basePrice = String(regular?.value?.centAmount ?? '');

  const productObject: IGtmProductBilling = {
    id: item?.productKey || '',
    name: item?.name || '',
    variant: variationId,
    category: item?.productType?.key || '',
    brand: getAttributeValue(item, 'brand') || '',
    price: calculateEventPrice(prices, isCencoPrice),
    dimension2: getAttributeValue(item, 'departmentId') || '',
    dimension3: '',
    dimension11: '',
    dimension32: getAttributeValue(item, 'classId') || '',
    dimension12: '',
    dimension33: getAttributeValue(item, 'classId') || '',
    dimension17: getShippingMethodsLabelsByDeliveryGroups(item?.deliveryGroups),
    dimension18: getAttributeValue(item, 'size') || '',
    dimension19: basePrice,
    dimension20: prices?.offer?.value?.centAmount || 0,
    dimension21: !!item?.variant?.warranties?.length,
    dimension30: getAttributeValue(item, 'brand') || '',
    dimension41: '',
    dimension42: 0,
    quantity: 1,
    dimension76: false
  };

  return productObject;
};

const createGTMProductWebViewObject = (
  item: CartLineItem,
  isCencoPrice: boolean
): IWebViewGtmProductBilling => {
  return {
    AnalyticsParameterItemID: item?.productKey || '',
    SearchParameterItemID: item?.productKey || '',
    AnalyticsParameterItemVariant: item?.variant?.sku || '',
    AnalyticsParameterItemName: item?.name || '',
    AnalyticsParameterItemCategory: item?.productType?.key || '',
    AnalyticsParameterItemCategory2: '(not set)',
    AnalyticsParameterItemCategory3: '(not set)',
    AnalyticsParameterItemCategory4: '(not set)',
    AnalyticsParameterItemCategory5: '(not set)',
    AnalyticsParameterItemBrand: getAttributeValue(item, 'brand') || '',
    AnalyticsParameterQuantity: item?.quantity || 1,
    // eslint-disable-next-line camelcase
    tipo_envio: getShippingMethodsLabelsByDeliveryGroups(item?.deliveryGroups),
    AnalyticsParameterPrice: calculateEventPrice(
      item?.variant?.prices,
      isCencoPrice
    )
  };
};

function getAttributeValue(
  item: CartLineItem,
  attributeName: string
): string | undefined {
  const attr = item?.variant?.attributes?.find(
    (attributeItem: {
      name: string;
      value: string | boolean | { key: string; label: string } | string[];
    }) => attributeItem.name === attributeName
  );

  if (!attr) {
    return undefined;
  }

  // Ahora discriminamos el tipo de 'value'
  if (typeof attr.value === 'string') {
    return attr.value;
  }

  // Si es un objeto con key y label, por ejemplo, retornamos la key
  if (
    typeof attr.value === 'object' &&
    !Array.isArray(attr.value) &&
    'key' in attr.value
  ) {
    return attr.value.key;
  }

  return undefined;
}

export const createProductObj = (
  products: CartLineItem[],
  isWebView: boolean,
  isCencoPrice: boolean = false
) => {
  return products?.map((item) =>
    isWebView
      ? createGTMProductWebViewObject(item, isCencoPrice)
      : createGTMProductObject(item, isCencoPrice)
  );
};

export const createEventPayload = (
  productsObj: (IWebViewGtmProductBilling | IGtmProductBilling)[],
  isWebView: boolean,
  totalPrice?: number,
  cart?: Cart
): IGTMSendProductsBilling => {
  const { discountCodes } = cart || {};
  const discountCode = discountCodes?.[0]?.discountCode?.obj?.code || '';

  const typeUser: GtmTypeUser = cart?.customerId
    ? GtmTypeUser.Registered
    : GtmTypeUser.Guest;

  if (isWebView) {
    return {
      event: 'add_payment_info',
      'type-User': typeUser,
      ecommerce: {
        checkout: {
          actionField: BILLING_ACTION_FIELD,
          paymentParams: {
            AnalyticsParameterCurrency: 'CLP',
            AnalyticsParameterValue: totalPrice || 0,
            AnalyticsParameterCoupon: discountCode || '',
            AnalyticsParameterPaymentType: ''
          },
          products: productsObj,
          documentType: cart?.shippingAddress?.documentType ?? 'RUT'
        }
      }
    };
  }
  return {
    event: 'checkout',
    'type-User': typeUser,
    ecommerce: {
      checkout: {
        actionField: {
          step: '3',
          option: 'Configure Payment',
          action: GtmEventInteractiveAction.Checkout
        },
        products: productsObj,
        documentType: cart?.shippingAddress?.documentType ?? 'RUT'
      }
    }
  };
};
